import {
  FilingCompletenessCategory,
  WarningAction,
  WarningDisposition,
} from '../generated/enums.gen';

// FPD-005a: shape lock for the warning-DTO contract that lets FPD-002 (attestation
// modal + evidence persistence) and FPD-005 (alert-don't-block preflight + Heads-up
// card) develop in parallel.
//
// Mirrors apps/api/Features/Finance/Filing/PreflightWarning.cs. Each enum value's
// string equals the C# PascalCase constant; the API serializes enums via
// JsonStringEnumConverter (Program.cs lines 47-58) so server-emitted JSON parses
// byte-equal into the interfaces below. Drift fails:
//   - Fpd005aWarningDtoContractTests on the C# side
//   - preflightTypes.test.ts on the TS side
//
// No `WarningSeverity` field. Severity is implicit in `availableActions`: a warning
// that only offers `Dismiss` is informational; one that offers `Attest` is something
// the treasurer should justify; one that offers `Resolve` has a concrete fix path.

/** The built-in preflight warning codes (ADR-025 open string contract). Mirrors the
 *  `WarningType` const-string members in PreflightWarning.cs. Not exhaustive of the
 *  wire — a pack may declare any string code; see the {@link WarningType} type alias below. */
export const WarningType = {
  /** A `Pending` contribution falls inside the filing period and is not covered by
   *  a `FilingExceptionAttested` event — the builder silently omits it. */
  PendingInPeriod: 'PendingInPeriod',
  /** A `Confirmed` contribution whose current `ProcessorStatus` is no longer in the
   *  adapter-defined filable set (e.g., flipped to `Voided` after Confirm). */
  ConfirmedNotFilableNow: 'ConfirmedNotFilableNow',
  /** Prior-period `Reported` contribution past the 14-day closure-obligation threshold. */
  AgedOverdueUnsettled: 'AgedOverdueUnsettled',
  /** FPD-052 — a PaymentProcessor with FeeRate = 0 (and BalanceFormShape ≠ NotApplicable)
   *  has at least one in-period contribution; fees won't appear on the filing
   *  unless the treasurer edits the rate or attests. */
  ZeroRateProcessorHasContributions: 'ZeroRateProcessorHasContributions',
  /** ULL-002 (Decision 4) — a `Pending` expenditure in the filing period. After the
   *  status-aware inclusion gate (D7) a Pending expenditure no longer files; confirm
   *  it (Resolve) or dismiss. Non-attestable (expenditures have no attestable fields). */
  PendingExpenditureInPeriod: 'PendingExpenditureInPeriod',
  /** ULL-004 (D6) — a filed-set `Category = Other` expenditure whose free-text purpose
   *  is blank (or auto-filled "Other"). Soft, attestable warning that NEVER blocks
   *  (unlike the `MissingExpenditurePayeeAddress` hard-fail). Actions: Attest / Dismiss. */
  ExpenditurePurposeOther: 'ExpenditurePurposeOther',
  /** RFR-005 (Stage 3) — a `ContributionRefund` whose `RefundedAt` falls in the filing
   *  period. AL EDI is add-only and can't file a return, so the refund emits no line;
   *  the treasurer must record it in the FCPA portal and amend (Ala. Code §17-5-7.1).
   *  Soft, attestable, NEVER blocks, and non-auto-dismissable (explicit Attest or
   *  Dismiss-with-reason). Actions: Attest / Dismiss. */
  UnresolvedRefundReturn: 'UnresolvedRefundReturn',
  /** FEC-005 (S4) — the report's beginning cash-on-hand (F3 line 23) doesn't equal the
   *  prior filed report's ending cash-on-hand (line 27). The FEC validates this, so the
   *  gap is surfaced to reconcile or attest (e.g. an opening-balance correction).
   *  Federal-gated, soft, attestable, NEVER blocks. Actions: Attest / Dismiss. */
  BeginningCashOnHandDiscontinuity: 'BeginningCashOnHandDiscontinuity',
  // GitHub #48 — FederalUnsupportedContributorPath retired: a FederalTransfer now emits
  // Schedule A line 12 (SA12, entity COM), so the deferred-gap warning is obsolete.
  /** FEC-007 (104.7 best-efforts) — an itemized individual receipt (per-donor cycle
   *  aggregate > $200) missing employer/occupation with no recorded donor-info request.
   *  11 CFR 104.7 permits filing once a documented 30-day request exists, so this NEVER
   *  hard-fails — it clears when employer + occupation are present OR the request date is
   *  recorded. Federal-gated, soft, attestable. Actions: Attest / Dismiss. */
  FederalDonorInfoBestEfforts: 'FederalDonorInfoBestEfforts',
  /** A filed disbursement that will ITEMIZE and whose free-text purpose doesn't say why
   *  the money was spent — blank, or one of the descriptions 11 CFR 104.3(b)(3)(i)(B)
   *  names as insufficient ("miscellaneous", "expenses", "voter registration" and the
   *  rest). The FEC wants a specific SB purpose description ("media", "salary",
   *  "polling"), but the field is non-error, so this is a soft, attestable nudge that
   *  NEVER blocks. LoanRepayment rows are excluded (purpose implicit in SB19A).
   *  Federal-gated. The name predates the regulation's named list — "missing" means
   *  "doesn't say why", not "empty".
   *
   *  Itemization is the gate because that is what the purpose obligation attaches to: a
   *  payee whose election-cycle aggregate stays at or under the threshold rolls into the
   *  line-17 summary total with no SB record, so its purpose never reaches the FEC. The
   *  gate is the per-PAYEE cycle aggregate, not the row's own amount — several
   *  sub-threshold rows to one payee can cross it together and all of them itemize.
   *  Categories that itemize regardless of amount (transfer, other-disbursement) are
   *  always in scope.
   *
   *  `affectedRowIds` are EXPENDITURE ids. The close-hub ledger reads them to mark the
   *  offending row before filing; never re-derive the insufficient-purpose list on the
   *  client — it lives in `FederalPurposeDescriptions`.
   *  Actions: Attest / Dismiss. */
  FederalDisbursementPurposeMissing: 'FederalDisbursementPurposeMissing',
  /** FEC-009 (D10) — a candidate-loan Schedule C ledger whose numbers don't reconcile
   *  (outstanding balance ≠ original − paid so far). The treasurer maintains paid-to-date
   *  manually (a repayment is an SB19A expenditure + a manual balance update; v1 doesn't
   *  auto-link the repayment), so the running ledger can drift. Federal-gated, soft,
   *  attestable, NEVER blocks. Actions: Attest / Dismiss. */
  FederalLoanBalanceDivergence: 'FederalLoanBalanceDivergence',
  /** FEC-007 (Item 29-B) — an in-kind from a non-committee organization (partnership,
   *  LLC, tribe, unincorporated association) defaults to entity ORG on Schedule A line
   *  11(a)(i) — NOT a PAC (11c, which needs a registered committee id) or party
   *  committee (11b). Fires so the treasurer can verify the classification (and file in
   *  the FEC portal with the right committee shape if it really is a PAC/party). The ORG
   *  record validates without a committee id, so this NEVER blocks. Federal-gated, soft,
   *  attestable. Actions: Attest / Dismiss. */
  FederalInKindFromOrganization: 'FederalInKindFromOrganization',
  /** GitHub #63 / #84 — a filed, itemized federal PAC (line 11c) / party (line 11b) contribution
   *  whose contributor contact carries no valid FEC committee id (C + 8 digits), so the builder
   *  emits a blank donor-committee id. Corrected premise (GitHub #84, Web Check-verified): a BLANK
   *  committee id does NOT reject the filing — the FEC accepts it (a soft #026/#027 warning on 11c,
   *  silent on 12). So this is a soft, alert-don't-block nudge (a blank id may still draw a
   *  follow-up / RFAI letter); the mis-aimed #63 file-time hard block is deleted. The condition the
   *  FEC actually rejects — a well-formed but unregistered id — is surfaced separately by
   *  FederalCommitteeIdRegistrationUnverified. Scope is 11b/11c only (Web Check is silent on a blank
   *  SA12 id). The candidate self-fund is excluded (files on line 11d / CAN). Federal-gated, soft,
   *  attestable, NEVER blocks. Actions: Attest / Dismiss. */
  FederalMissingContributorCommitteeId: 'FederalMissingContributorCommitteeId',
  /** GitHub #84 (Part B) — a report that WILL emit ≥1 committee id: an itemized PAC (11c) / party
   *  (11b) / committee-transfer (line 12 / SA12) contribution whose contact carries a format-valid
   *  C######## id. Our builder only format-checks the id; the FEC additionally does a registry
   *  lookup, and a well-formed-but-unregistered (typo'd/wrong) id rejects the ENTIRE .fec with no
   *  in-band signal. Registration can't be verified in the pure preflight, so this is one
   *  aggregated, alert-don't-block advisory naming the affected rows: double-check each committee id
   *  before filing. Does NOT fire when no committee id will be emitted. affectedRowIds are
   *  CONTRIBUTION ids; the count is interpolated at emission. Federal-gated, soft, attestable, NEVER
   *  blocks. Actions: Attest / Dismiss. */
  FederalCommitteeIdRegistrationUnverified: 'FederalCommitteeIdRegistrationUnverified',
  /** AFC-005 (Finding 1, part 3) — a SINGLE in-period contribution OR loan at/over
   *  $20,000. Ala. Code §17-5-8.1(c) requires a separate Major Contribution Report
   *  within 2 business days of receiving one — a portal action (like a refund), so this
   *  periodic filing doesn't satisfy it. Per-transaction trigger (NOT the §17-5-8
   *  per-source calendar-year itemization aggregate). AL-gated, soft, attestable, NEVER
   *  blocks; non-auto-dismissable so a large gift/loan can't silently fall through.
   *  Actions: Attest / Dismiss. */
  MajorContribution: 'MajorContribution',
  /** A §17-5-8.1(c) over-limit contribution still `Confirmed` in this filing period whose
   *  rapid-report FilingObligation was generated-but-not-yet-marked-reported (GeneratedAt !=
   *  null, Status Pending/Overdue). The treasurer already produced (and presumably uploaded)
   *  the single-contribution rapid file; filing THIS period would upload the same contribution
   *  a SECOND time and double it on the append-only AL FCPA portal (ADR-020). Surfaces the
   *  about-to-double-file case so the treasurer marks it reported first. AL-gated, soft,
   *  attestable, NEVER blocks. affectedRowIds are CONTRIBUTION ids. Actions: Attest / Dismiss. */
  RapidReportGeneratedNotReported: 'RapidReportGeneratedNotReported',
  /** AFC-008 (Part B) — an AL filing includes ≥1 in-scope loan. A live FCPA-portal
   *  upload proved the portal mis-parses a loan receipt's guarantor blocks (it records
   *  the loan amount as the last guarantor amount), so the AL builder now SUPPRESSES
   *  guarantor emission (Part A). This warns the treasurer to confirm each loan amount
   *  imported correctly after upload AND re-add any guarantors in the portal's
   *  Guarantors section (Ala. Code §17-5-2(a)(3)(a)). AL-gated, soft, attestable, NEVER
   *  blocks; non-auto-dismissable so a loan can't silently fall through. affectedRowIds
   *  are LOAN ids. Actions: Attest / Dismiss. */
  AlabamaLoanPortalReview: 'AlabamaLoanPortalReview',

  /** Portal-forced (2026-06-18) — an AL filing includes ≥1 in-period In-Kind "Other"
   *  contribution (cbInKindNature=08). The FCPA portal requires an in-kind description
   *  for "Other", and the v6.0 EDI format has no field for it ("Description is
   *  required"), so the AL builder OMITS such rows. This warns the treasurer to add
   *  each omitted In-Kind Other contribution directly in the portal (it is NOT in the
   *  electronic file). AL-gated, soft, attestable, NEVER blocks. affectedRowIds are
   *  CONTRIBUTION ids. Actions: Attest / Dismiss. */
  AlabamaInKindOtherPortalEntry: 'AlabamaInKindOtherPortalEntry',

  /** FCG-007 (plan Item 5b) — an in-period loan that will file (SC/10 ledger or SA13A
   *  receipt) but whose lender has no name on record. The file can't be built without
   *  it (it throws at File time, a clean 400), so this warns the treasurer to add the
   *  lender's name BEFORE filing. No Resolve action — the fix is on the loan's lender
   *  contact, not in the preflight modal. Federal-gated, soft, attestable, NEVER blocks.
   *  affectedRowIds are LOAN ids. Actions: Attest / Dismiss. */
  FederalMissingLoanLenderName: 'FederalMissingLoanLenderName',

  /** (Repointed from the retired FederalNonCandidateLoanUnsupported hard-block.) A federal
   *  filing includes a loan from a NON-candidate lender — a bank or other third party
   *  (Loan.IsPersonalFunds == false) — that files a record this report. The loan's LEDGER
   *  now files (SC/10 line 13b + SA13B + the SB19B repayment split), so it's NOT a hard-block.
   *  But a lending-institution loan also needs a Schedule C-1 loan-agreement the builder can't
   *  emit (the FEC makes its signature / lending-rep fields required and the model has no
   *  signatory data), so the C-1 is a PORTAL HANDOFF — complete it in the FEC portal after
   *  upload. Soft, alert-don't-block. Federal-gated. affectedRowIds are LOAN ids.
   *  Actions: Attest / Dismiss. */
  FederalBankLoanNeedsScheduleC1: 'FederalBankLoanNeedsScheduleC1',

  /** AFU-010 (L3) — the report's computed ending cash-on-hand is negative. Usually a
   *  data-entry slip worth catching before filing (a missed contribution, a duplicated
   *  expenditure), but can be legitimate (a timing gap). Soft, attestable, NEVER blocks;
   *  whole-period figure so affectedRowIds is empty. NOT jurisdiction-gated. Actions:
   *  Attest / Dismiss. */
  NegativeCashOnHand: 'NegativeCashOnHand',

  /** This AL report is an amendment (OriginalReportId != null). AL amendments are
   *  portal-only edits/deletes done directly in the FCPA portal — NOT a file re-upload.
   *  The portal is append-only and does NOT dedup re-uploads (ADR-020, confirmed by a
   *  2026-06-29 live test), so re-uploading the full file would duplicate every row and
   *  double the period. This warns the treasurer of that workflow before File. AL-gated,
   *  soft, attestable, NEVER blocks (the File affordance always stays — blocking a legal
   *  amendment path is the silent surprise this batch removes). Whole-period figure so
   *  affectedRowIds is empty. Actions: Attest / Dismiss. */
  AlabamaAmendmentPortalWorkflow: 'AlabamaAmendmentPortalWorkflow',

  /** GitHub #24 (ADR-020 after-upload drift) — ≥1 contribution or expenditure in the
   *  filing period was mutated after being included in an Uploaded ExportBatch. The AL
   *  FCPA portal received the pre-mutation version; local books now differ. The fix is a
   *  portal amendment (the append-only portal can't be re-uploaded without doubling rows).
   *  AL-gated, soft, attestable, NEVER blocks; no report-status flip. PortalActionRequired
   *  is true; affectedRowIds can mix contribution AND expenditure ids.
   *  Actions: Attest / Dismiss. */
  AfterUploadDrift: 'AfterUploadDrift',

  /** FEC-026 (D1) — this federal report is an amendment but carries no FEC receipt ID
   *  (StateFilingReceiptId is blank). The federal builder threads an amendment to its
   *  original via the HDR Rpt ID; a blank id makes it an orphan amendment the FEC can't
   *  match. The F3 amendment HDR id is treasurer-entered after the original files in the
   *  FEC portal (like the F6 date), so this is a WARNING, not a builder hard-fail.
   *  Federal-gated, soft, attestable, NEVER blocks; whole-period figure so affectedRowIds
   *  is empty. Actions: Attest / Dismiss. */
  FederalAmendmentMissingReceiptId: 'FederalAmendmentMissingReceiptId',

  /** FEC-026 (D2) — a federal report whose report code (e.g. Q2, 12G) is blank or not a
   *  recognized FEC value. The builder emits seq-12 REPORT CODE from the report's code — a
   *  required, membership-validated FEC field — so a blank or non-member value yields a
   *  missing/invalid code. The amendment mint-site copies the original's code, so a
   *  well-formed amendment never trips this; the warning catches a report still lacking a
   *  valid code. Federal-gated, soft, attestable, NEVER blocks; whole-period figure so
   *  affectedRowIds is empty. Actions: Attest / Dismiss. */
  FederalReportCodeMissingOrInvalid: 'FederalReportCodeMissingOrInvalid',

  /** FEC-026 (D3) — an in-period non-Voided ContributionRefund whose Schedule B (SB20)
   *  payee address is blank (the linked contribution's contact has a blank
   *  address/city/state/zip, or the refund is an orphan with no contact at all). The
   *  builder sources the SB20 payee address from refund.Contribution?.Contact; a blank
   *  contact emits a blank SB20 address. The AL MissingExpenditurePayeeAddress check is
   *  AL-gated AND Expenditure-scoped, so federal contribution refunds had no payee-address
   *  nudge. Federal-gated (AL refunds are covered by UnresolvedRefundReturn), soft,
   *  attestable, NEVER blocks. affectedRowIds are REFUND ids. Actions: Attest / Dismiss. */
  FederalRefundPayeeAddressMissing: 'FederalRefundPayeeAddressMissing',

  /** A receipt that files a Schedule A record whose contributor has no complete mailing address.
   *  Follows the builder's emission rule (committee, party, candidate and transfer receipts at any
   *  amount; an individual once itemized). Federal-gated, soft, attestable, NEVER blocks.
   *  affectedRowIds are CONTRIBUTION ids; the count is interpolated. Actions: Attest / Dismiss. */
  FederalContributorAddressMissing: 'FederalContributorAddressMissing',

  // #346 removed FederalRefundLineMisroute — every refunded contributor subtype now routes to an
  // explicit Schedule B line (SB20A/B/C), so there is no silent mis-line to warn about. The
  // placeholder never shipped (its #344 introduction was un-deployed), so no disposition depends on it.

  /** A filed contribution that arrived through a conduit processor — ActBlue or WinRed
   *  — derived from ImportBatch.Source → ImportSourceToProcessorKind. ActBlue/WinRed are
   *  FEC-registered conduits, so the gift is an earmarked pass-through (11 CFR 110.6):
   *  the original donor AND the intermediary processor must be disclosed. v1 files it as
   *  a plain Schedule A line-11a individual receipt — the dollars are right but the
   *  conduit/intermediary disclosure isn't auto-emitted; this warning makes that deferred
   *  gap loud (it does not emit the disclosure). One aggregated warning (affectedRowIds are
   *  contribution ids); the treasurer adds the processor disclosure in the filing portal.
   *  Federal-gated, soft, attestable, NEVER blocks. Actions: Attest / Dismiss. */
  FederalEarmarkedConduitDeferred: 'FederalEarmarkedConduitDeferred',

  /** A federal AMENDMENT whose filed (frozen-basis) loan balance diverges from the current
   *  live Loan.OutstandingBalance — the silent strike case: the treasurer zeroed the live
   *  loan to remove an erroneously-entered loan, but federal's
   *  amendmentLoanBalanceBasis = FrozenPeriodClose policy re-materializes it from the frozen
   *  SubmissionPackageLoan snapshot of the superseded package, so the F3A still reports it on
   *  line 10 / Schedule C at its prior-period balance with no alert. This makes that silent
   *  re-materialization loud (the full strike/void affordance is a later slice — this
   *  surface does NOT remove the loan). The signal is frozen-filed-balance ≠ live-balance, an
   *  amendment-specific check, NOT the internally-consistent FederalLoanBalanceDivergence
   *  ledger check (and it does not double-warn with it — that check's `> 0` guard skips the
   *  zeroed live loan anyway). Fires only on an amendment (a frozen snapshot exists) and only
   *  on the strike signal (frozen > 0 while live = 0 or deleted). A legitimate
   *  repayment-after-filing that leaves the loan still outstanding (live > 0) is the
   *  carry-forward case the policy serves and does NOT fire. Federal-gated
   *  (AL is LiveCurrent: a zeroed loan naturally drops, no re-materialization). affectedRowIds
   *  are LOAN ids. Soft, attestable, NEVER blocks. Actions: Attest / Dismiss. */
  FederalAmendmentLoanRemovalDivergence: 'FederalAmendmentLoanRemovalDivergence',

  /** Strategy-3 money-conservation safety net — the report's FILED summary totals (F3
   *  line 16 total receipts, line 22 total disbursements) don't account for all the
   *  dollars in the Confirmed-period ledger (within $0.01). An independent re-sum of the
   *  Confirmed ledger — contributions + in-period loans on the receipt side; expenditures
   *  + synthesized processor fees + in-kind FMV offset + refunds on the disbursement side
   *  — is compared against the filed totals; a divergence fires this warning naming the
   *  unaccounted dollar amount. Scope is MONEY CONSERVATION ONLY (silent shape-(a): a
   *  Confirmed transaction whose money vanishes between the ledger and the filed totals —
   *  an assembly-layer drop or a future un-summed regime); it does NOT catch wrong-line
   *  mis-routing or missing memo rows (shape-(b), the real-filing oracle's job). Whole-period
   *  figure so affectedRowIds is empty (the body states the amount). Federal-gated, soft,
   *  attestable, NEVER blocks. Actions: Attest / Dismiss. */
  FederalReportDoesNotReconcile: 'FederalReportDoesNotReconcile',

  /** A filed contribution whose gift shape implies an individual (the contributor's
   *  ContactKind is Individual) yet whose contributor on file is an Organization contact — a
   *  data-integrity mismatch between the gift and the contributor record. Every itemized receipt distinguishes an
   *  individual from an organization (AL EDI, for one, emits the individual source code with
   *  an org-name block and no individual name parts when they disagree), so the row must be
   *  reconciled before it files. The system does NOT auto-split the contributor's name blob or
   *  auto-change the contact kind (no inference); it surfaces the mismatch and the treasurer
   *  resolves it — make the contact an individual (with a first and last name) OR change the
   *  contribution to a real organization type (PAC/party/corporate) via the contact detail
   *  Individual↔Organization toggle. No Resolve action (the fix is on the contact record).
   *  NOT jurisdiction-gated (like NegativeCashOnHand — the mismatch is wrong wherever the
   *  filing distinguishes the two). One aggregated warning; affectedRowIds are CONTRIBUTION
   *  ids. Soft, attestable, NEVER blocks. Actions: Attest / Dismiss. */
  IndividualContributionOnOrganizationContact: 'IndividualContributionOnOrganizationContact',

  /** Issue #350 — one or more organization contacts carry an unresolved contact-merge
   *  OrganizationType conflict (#348 kept the surviving record's subtype but couldn't tell which
   *  was right). The type decides the filing line, so it's surfaced before filing as the backstop
   *  to the inline contact-page fix-it alert. Audit-derived from the SAME shared source the inline
   *  surface reads (resolving/dismissing in either place clears both); re-fires until resolved.
   *  Federal + AL gated. affectedRowIds are surviving CONTACT ids. Soft,
   *  attestable, NEVER blocks. Actions: Attest / Dismiss. */
  UnresolvedOrganizationTypeConflicts: 'UnresolvedOrganizationTypeConflicts',

  /** GitHub #49 — a filed transfer to another committee now files correctly on Schedule B line 18
   *  (SB18, the disbursement-side mirror of the SA12 transfer-IN receipt), but when its payee lacks
   *  a valid FEC committee id (C + 8 digits) the builder omits the recipient beneficiary block, so
   *  the recipient committee isn't fully identified. The transfer STILL files — this is the
   *  disbursement-side analog of the donor-side FederalMissingContributorCommitteeId; the remedy is
   *  an in-app data check (add the committee id to the payee's contact). Fires only for transfers
   *  whose payee is missing/has a malformed id. Federal-gated. affectedRowIds are EXPENDITURE ids;
   *  the count is interpolated at emission. Soft, attestable, NEVER
   *  blocks. Actions: Attest / Dismiss. */
  FederalTransferOutMissingCommitteeId: 'FederalTransferOutMissingCommitteeId',

  /** #711 — a filed federal transfer IN recorded before the app started asking what makes it
   *  exempt. Marking a receipt a federal transfer used to be enough on its own to clear every
   *  contribution limit AND claim Schedule A line 12 — a line that asserts the money came from
   *  another authorized committee of the same candidate. Unanswered rows keep exactly today's
   *  treatment (exempt, line 12), because re-lining a receipt an earlier report already filed
   *  there would change what the committee said it filed; this surfaces them for review instead.
   *  Federal-gated. affectedRowIds are CONTRIBUTION ids; the count is interpolated at emission.
   *  Soft, attestable, NEVER blocks. Actions: Attest / Dismiss. */
  FederalTransferQualificationUnreviewed: 'FederalTransferQualificationUnreviewed',

  /** ADR-030 superseded — the POSITIVELY-marked committee case (companion to
   *  FederalTransferOutMissingCommitteeId, which covers the un-marked transfer). A committee-to-
   *  committee disbursement (SB18 transfer / SB21 other disbursement) or a refund to a committee
   *  (SB20B/SB20C) that names a committee beneficiary — via a committee OrganizationType on the payee or an
   *  explicit beneficiary link — but omits its recipient FEC id. Was ADR-030 §3's file-time HARD
   *  block; downgraded to this soft attestable warning because the recipient committee id is NOT a
   *  required FEC disclosure (11 CFR 100.12; FEC Form 3 line-18/20 instructions; the validator
   *  errors only on a malformed-present id). affectedRowIds are EXPENDITURE + refund ids — never
   *  CONTRIBUTION ids; the count is interpolated at emission.
   *  Federal-gated, soft, attestable, NEVER blocks. Actions: Attest / Dismiss. */
  FederalBeneficiaryCommitteeIdMissing: 'FederalBeneficiaryCommitteeIdMissing',

  /** F-U3 — an in-period filed contribution whose contact ALSO has an expenditure for this
   *  campaign is offset-shaped: money in from someone you pay looks like a refund/rebate of an
   *  operating expenditure, which legally belongs on Line 14 (offsets to operating expenditures),
   *  NOT a Line 11(a) contribution. This version can't file Line 14, so leaving it as a
   *  contribution overstates line 11a. This makes the likely misfile loud. The candidate contact
   *  is excluded (a self-fund receipt + a reimbursement/loan-repayment disbursement on the same
   *  candidate contact is normal), and processor fee-payee contacts are excluded. The
   *  expenditure-existence lookup is campaign-scoped, any date. May co-fire with
   *  IndividualContributionOnOrganizationContact. affectedRowIds are CONTRIBUTION ids; the count
   *  is interpolated at emission. Federal-gated, soft, attestable, NEVER blocks. Actions:
   *  Attest / Dismiss. */
  FederalReceiptFromVendorContact: 'FederalReceiptFromVendorContact',

  /** A federal contributor whose per-election aggregate exceeds the applicable federal
   *  contribution limit ($3,500/election for individuals in 2025). The entry-time compliance
   *  gate flags this as OverContributionLimit, but that gate is soft (a treasurer can
   *  override-accept it) and an excess can also arrive via import and bypass entry, so at
   *  filing time there was no over-limit signal. This surfaces the excess before filing,
   *  naming the donor, the election, the aggregate, and the amount over, with the legal
   *  remedy: federal limits are per election, so the excess must be refunded, redesignated to
   *  another election, or reattributed to another contributor within 60 days of receipt
   *  (11 CFR 103.3(b), 110.1(b)(3)). The limit + aggregate come from the same source the
   *  entry-time gate uses, so the two surfaces can't disagree on the number. Federal-gated,
   *  soft, attestable, NEVER blocks; affectedRowIds are the CONTRIBUTION ids for that
   *  donor+election. The server fully interpolates the body (donor/election/figures) at
   *  emission. Actions: Attest / Dismiss. */
  FederalContributionOverLimit: 'FederalContributionOverLimit',

  /** #749 — a party's two 52 U.S.C. § 30116(h) committees (its national committee and its
   *  senatorial campaign committee: DNC + DSCC, or RNC + NRSC) have TOGETHER passed the single
   *  combined ceiling the statute sets for the pair, measured across the candidate's whole
   *  campaign rather than per election. Neither the entry-time gate nor
   *  FederalContributionOverLimit can see this: both price ONE contact against ONE election,
   *  and this ceiling pools two committees over every election. The pooled figure is summed
   *  from the contribution rows campaign-wide, net of refunds and excluding only voided rows,
   *  so the warning clears when the treasurer records the refund — which is the only cure, since
   *  a redesignation cannot move a total that already spans every election. Senate-gated (the
   *  statute is a Senate provision), soft, attestable, NEVER blocks. A whole-campaign
   *  condition, so affectedRowIds is EMPTY. The server fully interpolates the body (the
   *  contributing committees, each with its own figure, plus the totals) at emission.
   *  Actions: Attest / Dismiss. */
  FederalPartyPairOverCombinedCeiling: 'FederalPartyPairOverCombinedCeiling',

  /** Virginia D5 — an itemized individual Schedule A/B contributor (per-donor calendar-year
   *  aggregate strictly > $100) missing employer or occupation. Organizations are excluded; a
   *  documented employerInfoRequestDate is the best-efforts escape. A rolled-up count with NO
   *  percentage (strictly stronger than COMET's 80% bar, avoiding the uncertain denominator).
   *  VA-gated, soft, attestable, NEVER blocks. affectedRowIds are CONTRIBUTION ids. Actions:
   *  Attest / Dismiss. */
  VirginiaContributorEmployerOccupationMissing: 'VirginiaContributorEmployerOccupationMissing',

  /** North Carolina (#288) — an in-period contribution has no election designation
   *  (primary/runoff/general). NC buckets the $6,800 limit and the $50 itemization "Sum to Date"
   *  per election, so an undesignated gift can't be bucketed. Surfaced like a missing
   *  occupation/employer (never auto-bucketed or date-inferred) for the treasurer to fix or attest.
   *  NC-gated, soft, attestable, NEVER blocks. affectedRowIds are CONTRIBUTION ids. Actions:
   *  Attest / Dismiss. */
  NcContributionUndesignated: 'NcContributionUndesignated',

  /** Federal (#742, ADR-080) — an in-period gift the donor did NOT designate arrived within
   *  14 days AFTER one of the committee's elections, so it may be bucketed against the wrong
   *  election: we attribute undesignated money by the date it ARRIVED, while 11 CFR
   *  110.1(b)(2)(ii) keys on when it was MADE. No made date is stored (nothing collects a
   *  postmark), so the committee is asked. The remedy is the DATE, never the designation —
   *  recording an election the donor never gave is a disclosable redesignation.
   *  Federal-gated, soft, attestable, NEVER blocks. affectedRowIds are CONTRIBUTION ids.
   *  Actions: Attest / Dismiss. */
  FederalUndesignatedGiftAfterElection: 'FederalUndesignatedGiftAfterElection',

  /** Maryland (MDCRIS, ADR-073) — a filed expenditure with no valid coded MDCRIS
   *  Expense Category → Purpose pair (unclassified, or a stored pair that isn't a listed cascade
   *  combination). The MD builder resolves such a row to the safe "Other Expenses / Other" default,
   *  so the uploaded file is always valid — but "Other" discloses nothing useful, so this nudges the
   *  treasurer to classify the row precisely. MD-gated, soft, attestable, NEVER blocks. affectedRowIds
   *  are EXPENDITURE ids; the count is interpolated at emission. Actions: Attest / Dismiss. */
  MarylandExpensePurposeUnrefined: 'MarylandExpensePurposeUnrefined',

  /** Maryland (MDCRIS, ADR-073, #588) — the committee has ≥1 in-period loan, but v1 does NOT generate
   *  the MDCRIS Loan workbook (loans need fields we don't model, and fabricating them would invent
   *  required disclosure). So the loan is not in the uploaded file; the treasurer enters it directly in
   *  the MDCRIS portal after upload. The MD analog of the VA co-borrower portal hand-off:
   *  PortalActionRequired. MD-gated, soft, attestable, NEVER blocks. affectedRowIds are LOAN ids;
   *  the count is interpolated at emission. Actions: Attest / Dismiss. */
  MarylandLoanPortalEntry: 'MarylandLoanPortalEntry',

  /** Maryland (MDCRIS, ADR-073, #588) — this period has no contributions, outstanding obligations, or
   *  expenditures to upload, so the builder produces no import workbook (an MDCRIS upload carries
   *  transactions; an empty report is e-signed directly in MDCRIS regardless). Heads-up that there is
   *  nothing to upload and to file + e-sign the empty report directly in the MDCRIS portal. MD-gated,
   *  soft, NEVER blocks. Whole-period, so no affectedRowIds. Actions: Attest / Dismiss. */
  MarylandNothingToUpload: 'MarylandNothingToUpload',

  /** A contributor's cash (bills or coins) in one window of an overridable aggregate cash cap
   *  (Maryland's $100 per cycle, ADR-073) is over the cap after refunds, with no override reason on
   *  record (ADR-040). Soft, attestable, NEVER blocks. affectedRowIds are CONTRIBUTION ids.
   *  Actions: Attest / Dismiss. */
  CashOverAggregateCapWithoutReason: 'CashOverAggregateCapWithoutReason',

  /** Virginia D6 (warn half) — one or more values the builder will emit exceed their COMET XSD
   *  maxLength (purpose/product 150, names/employer/address/city 100, occupation 50, suffix 30,
   *  ZIP 10). The builder truncates name/address/product to stay schema-valid (silently dropping
   *  disclosure) and emits the free-text purpose/authorizing name faithfully (an over-length one
   *  rejects the whole file at COMET), so this alerts the treasurer to trim first. A rolled-up
   *  count; whole-report (no affectedRowIds). VA-gated, soft, attestable, NEVER blocks. */
  VirginiaOverLengthValue: 'VirginiaOverLengthValue',

  /** Virginia — a schedule entity's raw State is present but not a member of COMET's closed
   *  stateCodeType enum, so it fell back to the XSD's N/A member (the disclosed state is lost).
   *  A rolled-up count of entities whose state degraded to N/A (a blank state is the address
   *  hard-stop, not this warn). VA-gated, soft, attestable, NEVER blocks; whole-report (no
   *  affectedRowIds). */
  VirginiaUnmappableState: 'VirginiaUnmappableState',

  /** ADR-037 — the committee's stored election-cycle start reaches back before the earliest
   *  activity we hold, so this cycle may include pre-onboarding contributions/expenditures not
   *  reflected in the totals or per-donor itemization (a mid-cycle-onboarding data-availability
   *  limit). VA-gated, soft, attestable, NEVER blocks; whole-report (no affectedRowIds). Distinct
   *  from the missing-cycle-start HARD stop (a fileability gate, surfaced as a hard failure). */
  VirginiaCycleStartPredatesData: 'VirginiaCycleStartPredatesData',

  /** GitHub #150 / ADR-038 — the treasurer is filing their periodic report while an in-window
   *  §24.2-947.9 rapid large-contribution obligation for an in-period gift is still unfiled. Unlike
   *  Alabama's generated-not-reported warning (a double-upload concern), Virginia is whole-report, so
   *  the gift belongs on this periodic report either way — the concern is the SEPARATE next-day rapid
   *  filing the treasurer still owes COMET. VA-gated, soft, attestable, NEVER blocks. */
  VirginiaRapidReportUnfiled: 'VirginiaRapidReportUnfiled',

  /** GitHub #182 — the Louisiana analog of VirginiaRapidReportUnfiled: the treasurer is filing their
   *  periodic report while an in-window R.S. 18:1495.4 rapid large-contribution obligation for an
   *  in-period gift (received in an election's 20-day pre-election window) is still unfiled. Louisiana
   *  files the gift on the periodic report either way; the concern is the SEPARATE rapid filing the
   *  treasurer still owes the LEADERS portal. LA-gated, soft, attestable, NEVER blocks. */
  LouisianaRapidReportUnfiled: 'LouisianaRapidReportUnfiled',

  /** Louisiana — one or more values the .PDP builder will emit are longer than the LEADERS vendor
   *  spec's maximum for their column (a name 90, an address line 45, a city 30, a state 2, an
   *  expenditure purpose 180), so what Louisiana stores may differ from what we believe we filed.
   *  Neither side of the wire enforces the caps today: the builder writes values through as-is and
   *  the LEADERS validator accepted a 101-character name against a 90-character cap in a live run.
   *  Measured on the COMPOSED, TRANSLITERATED value the builder emits, not the stored column.
   *  Covers schedule rows AND the settings-derived cover fields (which carry no row id and are
   *  named in the text). A rolled-up count. LA-gated, soft, attestable, NEVER blocks — LA is
   *  uploadIsFiling:false, so an enforce here would refuse to produce a file at all for something
   *  LEADERS accepts. affectedRowIds mixes row kinds. */
  LouisianaOverLengthValue: 'LouisianaOverLengthValue',

  /** Louisiana — a stored value the .PDP builder will emit as an EMPTY string, so it files BLANK on
   *  a public disclosure. AsciiTransliterator drops a character with no honest Latin spelling (CJK,
   *  Greek, Cyrillic, Arabic, Hebrew) rather than substitute "?", so a value written wholly in such
   *  a script transliterates to nothing. The stored contact/setting keeps its real spelling and the
   *  donor-completeness check passes on it — the emptiness appears only at the emit boundary, and
   *  LEADERS validates neither presence nor length, so the blank surfaces on the rendered form. The
   *  remedy is the OPPOSITE of over-length (add a Latin spelling / type it into LEADERS, not trim),
   *  so it carries its own copy. Measured on the load-bearing stored component, not the composed
   *  caret form (a wholly-non-Latin name composes to "^^^", whose carets survive). LA-gated, soft,
   *  attestable, NEVER blocks — LA is uploadIsFiling:false. affectedRowIds mixes row kinds. */
  LouisianaTransliteratedEmptyValue: 'LouisianaTransliteratedEmptyValue',

  /** An annual Louisiana Form 102 whose cover would carry a BLANK planned-candidacy statement
   *  (CVR col 40). The spec repurposes the notary slot on an ANN report — "planned election
   *  date/year or other statement regarding planned candidacy or lack thereof" — and the builder
   *  derives it from the nearest configured election date after the period. Every date already
   *  past means nothing to derive. Soft, attestable, never blocks (LEADERS accepts the file and
   *  the column is optional to its validator). */
  LouisianaPlannedCandidacyMissing: 'LouisianaPlannedCandidacyMissing',

  /** GitHub #151 / ADR-039 — the treasurer is filing their periodic report while a §24.2-947.11
   *  pre-session large-contribution obligation (due Jan 15) is still unfiled. The pre-session gifts
   *  also appear on this periodic report (supplemental, non-flip); the concern is the SEPARATE special
   *  report the treasurer still owes COMET. Campaign-wide (per-donor aggregate), soft, attestable. */
  VirginiaPreSessionReportUnfiled: 'VirginiaPreSessionReportUnfiled',

  /** Virginia §24.2-947.4 — a loan received this period (a Schedule E "R" row) has a
   *  guarantor/co-borrower on file. Virginia requires disclosing them, but v1 does NOT emit
   *  COMET's CoBorrower element (our guarantor record is flat AL-shaped, COMET's is structured;
   *  the XSD caps it at one; AL's own guarantor emission is suppressed for a portal bug), so this
   *  warns the treasurer to add the co-borrower directly in COMET. portalActionRequired: the safe
   *  order is upload → add the co-borrower in COMET → submit (a re-upload supersedes the whole
   *  report and wipes the manual add, ADR-036). The file stays XSD-valid (CoBorrower is optional).
   *  Fires only for loans reported this period. VA-gated, soft, attestable, NEVER blocks.
   *  affectedRowIds are LOAN ids. Actions: Attest / Dismiss. (ADR-044) */
  VirginiaLoanCoBorrowerNotFiled: 'VirginiaLoanCoBorrowerNotFiled',

  /** Virginia §24.2-947.4 — a loan was fully repaid to a $0 balance this period. A loan forgiven
   *  to exactly $0 is indistinguishable from a genuine full repayment by the ledger math, so the
   *  FederalLoanBalanceDivergence check (which needs a remaining balance > 0) can't catch it. This
   *  gentle reminder closes that blind spot: if any of the payoff was forgiveness, Virginia
   *  requires recording the forgiven amount as both a contribution and a loan repayment. It fires
   *  on every full payoff (we can't tell forgiveness from repayment without a reason model), so
   *  it's a one-click Dismiss for a genuine repayment. Mutually exclusive with
   *  FederalLoanBalanceDivergence (that needs balance > 0; this needs balance == 0). The remedy is
   *  in-app (record the two rows), not a portal action. VA-gated, soft, attestable, NEVER blocks.
   *  affectedRowIds are LOAN ids. Actions: Attest / Dismiss. (ADR-044) */
  VirginiaLoanRepaidForgivenessReminder: 'VirginiaLoanRepaidForgivenessReminder',
} as const;

/**
 * ADR-025 — the warning identifier is an OPEN string code on the wire, not a closed
 * enum: a pack declares its own codes (namespaced, e.g. `'IL.TotalsMissingBegFunds'`).
 * The {@link WarningType} const above lists the built-in codes for reference and the
 * escape-hatch copy registry; ANY string is a valid code, and the generic renderer
 * handles a code this bundle has never heard of. Membership is no longer pinned.
 */
export type WarningType = string;

// ADR-025 — the "mandatory post-upload portal action" signal is now a per-warning WIRE
// FLAG (`PreflightWarning.portalActionRequired`) the server sets, NOT a shared set of
// warning-type identities. Both consumers (the HeadsUp card's grouping and the
// FilePreflightModal auto-dismiss guard) read the flag, so they still can't drift — the
// coupling just moved onto the wire, off a hard-coded identity list. The former
// `PORTAL_ACTION_REQUIRED_WARNING_TYPES` set + `isPortalActionRequiredWarning` helper are
// deleted (a new state's portal-action warnings need zero shared edits now).

// `SubmissionPackageExceptionType` is owned by `../types/finance` (FPD-001
// shipped it there for the existing FilingAttestationRequest contract); we
// re-import rather than redefine.
import type { SubmissionPackageExceptionType } from '../generated/enums.gen';
// The per-processor fee status line is defined once in sortInbox.ts (the close-hub band's wire
// type). The preflight payload carries the SAME shape scoped to the report's period, so re-import
// rather than redefine (all domain types come from one place).
import type { ProcessorFeeStatusLine } from '../generated/dtos.gen';
